import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import apiService from '../../services/api';
import {
  ArrowLeft,
  Printer,
  Upload,
  X,
} from 'lucide-react';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { renderRichContent } from '../../utils/richText';

// --- PORTAL COMPONENT ---
const PrintPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mountNode, setMountNode] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const el = document.createElement('div');
    el.id = 'print-portal-root';
    document.body.appendChild(el);
    setMountNode(el);
    return () => {
      document.body.removeChild(el);
    };
  }, []);

  if (!mountNode) return null;
  return createPortal(children, mountNode);
};

const ViewQuotationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quotation, setQuotation] = useState<any>(null);
  const [activeView, setActiveView] = useState<'summary' | 'combined' | number>('summary');
  
  // Image State
  const [headerImage, setHeaderImage] = useState<string | null>(null);
  const [footerImage, setFooterImage] = useState<string | null>(null);
  const headerInputRef = useRef<HTMLInputElement>(null);
  const footerInputRef = useRef<HTMLInputElement>(null);

  // --- Data Fetching ---
  const fetchQuotation = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await apiService.getQuotation(id);
      setQuotation(response.data);
    } catch (error: any) {
      console.error('Error fetching quotation:', error);
      toast.error(error.response?.data?.error || 'Failed to load quotation');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchQuotation();
    const savedHeader = localStorage.getItem('quotation-header-image');
    const savedFooter = localStorage.getItem('quotation-footer-image');
    if (savedHeader) setHeaderImage(savedHeader);
    if (savedFooter) setFooterImage(savedFooter);
  }, [id, fetchQuotation]);

  // If summary is disabled and we're on summary view, switch to first schedule
  useEffect(() => {
    if (quotation && quotation.enableSummary === false && activeView === 'summary' && quotation.schedules?.length > 0) {
      setActiveView(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotation?.enableSummary, quotation?.schedules?.length, activeView]);

  // --- Image Handlers ---
  const handleHeaderImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setHeaderImage(result);
        localStorage.setItem('quotation-header-image', result);
        toast.success('Header image uploaded');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFooterImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setFooterImage(result);
        localStorage.setItem('quotation-footer-image', result);
        toast.success('Footer image uploaded');
      };
      reader.readAsDataURL(file);
    }
  };

  const removeHeaderImage = () => {
    setHeaderImage(null);
    localStorage.removeItem('quotation-header-image');
    toast.success('Header image removed');
  };

  const removeFooterImage = () => {
    setFooterImage(null);
    localStorage.removeItem('quotation-footer-image');
    toast.success('Footer image removed');
  };

  // --- Helpers ---
  const numberToWords = (num: number): string => {
    if (num === 0) return 'Zero';
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    const convertLessThanThousand = (n: number): string => {
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '');
    };
    
    if (num < 1000) return convertLessThanThousand(num);
    if (num < 100000) {
      const thousands = Math.floor(num / 1000);
      const remainder = num % 1000;
      return convertLessThanThousand(thousands) + ' Thousand' + (remainder !== 0 ? ' ' + convertLessThanThousand(remainder) : '');
    }
    if (num < 10000000) {
      const lakhs = Math.floor(num / 100000);
      const remainder = num % 100000;
      return convertLessThanThousand(lakhs) + ' Lakh' + (remainder !== 0 ? ' ' + numberToWords(remainder) : '');
    }
    
    const crores = Math.floor(num / 10000000);
    const remainder = num % 10000000;
    return convertLessThanThousand(crores) + ' Crore' + (remainder !== 0 ? ' ' + numberToWords(remainder) : '');
  };

  const calculateScheduleTotals = (schedule: any) => {
    let totalAmount = 0;
    let totalVatAmount = 0;

    schedule.sections?.forEach((section: any) => {
      section.products?.forEach((product: any) => {
        totalAmount += product.total || 0;
        totalVatAmount += product.vatAmount || 0;
      });
    });

    const cfrCtgAmount = schedule.cfrCtgAmount || 0;
    const baseTotal = schedule.vatInclusion ? totalAmount : (totalAmount + totalVatAmount);
    const grandTotal = baseTotal + cfrCtgAmount;

    return { totalAmount, totalVatAmount, cfrCtgAmount, grandTotal };
  };

  const calculateOverallTotals = () => {
    const byCurrency: { [key: string]: { total: number; vat: number; cfrCtg: number; grand: number } } = {};
    quotation?.schedules?.forEach((schedule: any) => {
      const currency = schedule.currency || 'BDT';
      if (!byCurrency[currency]) {
        byCurrency[currency] = { total: 0, vat: 0, cfrCtg: 0, grand: 0 };
      }
      const totals = calculateScheduleTotals(schedule);
      byCurrency[currency].total += totals.totalAmount;
      byCurrency[currency].vat += totals.totalVatAmount;
      byCurrency[currency].cfrCtg += totals.cfrCtgAmount || 0;
      byCurrency[currency].grand += totals.grandTotal;
    });
    return byCurrency;
  };

  const handlePrint = () => {
    if (!quotation) {
      toast.error('Quotation data not loaded');
      return;
    }
    window.print();
  };

  // Build combined view: merge same section name + same item into one row, with amounts per schedule in columns
  type SectionGroup = { sectionName: string; sectionDescription: string; items: Array<{ product: any; unit: string; bySchedule: Record<number, { qty: number; unitRate: number; total: number; vatOnRate?: number; vatAmount: number; customsDutyAmount: number }> }> };
  const buildCombinedMergedGroups = (): SectionGroup[] => {
    const sectionOrder: string[] = [];
    const sectionMap = new Map<string, { sectionDescription: string; items: Map<string, { product: any; unit: string; bySchedule: Record<number, { qty: number; unitRate: number; total: number; vatOnRate?: number; vatAmount: number; customsDutyAmount: number }> }> }>();
    const schedules = quotation?.schedules || [];
    schedules.forEach((schedule: any, scheduleIdx: number) => {
      schedule.sections?.forEach((section: any) => {
        const sectionName = section.sectionName || '';
        const sectionDescription = section.sectionDescription || '';
        if (!sectionMap.has(sectionName)) {
          sectionOrder.push(sectionName);
          sectionMap.set(sectionName, { sectionDescription, items: new Map() });
        }
        const group = sectionMap.get(sectionName)!;
        section.products?.forEach((prod: any) => {
          const pid = prod.product?._id || prod.product;
          if (!pid) return;
          const key = String(pid);
          const data = {
            qty: prod.qty ?? 0,
            unitRate: prod.unitRate ?? 0,
            total: prod.total ?? 0,
            vatOnRate: prod.vatOnRate ?? prod.vatRate,
            vatAmount: prod.vatAmount ?? 0,
            customsDutyAmount: prod.customsDutyAmount ?? 0,
          };
          if (group.items.has(key)) {
            group.items.get(key)!.bySchedule[scheduleIdx] = data;
          } else {
            group.items.set(key, {
              product: prod.product,
              unit: prod.unit || (prod.product?.unit?.symbol || prod.product?.unit?.name) || '-',
              bySchedule: { [scheduleIdx]: data },
            });
          }
        });
      });
    });
    return sectionOrder.map((name) => {
      const g = sectionMap.get(name)!;
      return {
        sectionName: name,
        sectionDescription: g.sectionDescription,
        items: Array.from(g.items.values()),
      };
    });
  };

  const renderProductDetails = (product: any) => {
    if (!product) return null;
    const getRefName = (ref: any) => {
      if (!ref) return '';
      if (typeof ref === 'object' && ref !== null && ref.name) return ref.name;
      return String(ref);
    };
    return (
      <div className="text-[9px] mt-1 space-y-0.5">
        {product.brand && <p><strong>Brand:</strong> {getRefName(product.brand)}</p>}
        {product.model && <p><strong>Model:</strong> {product.model}</p>}
        {product.type && <p><strong>Type:</strong> {getRefName(product.type)}</p>}
        {product.compressorType && <p><strong>Compressor Type:</strong> {getRefName(product.compressorType)}</p>}
        {product.countryOfOrigin && <p><strong>Country of Origin:</strong> {getRefName(product.countryOfOrigin)}</p>}
        {product.countryOfAssembly && <p><strong>Country of Assembly:</strong> {getRefName(product.countryOfAssembly)}</p>}
        {product.details && <p><strong>Details:</strong> {product.details}</p>}
        {product.description && <p><strong>Description:</strong> {product.description}</p>}
        {product.specifications && <p><strong>Specifications:</strong> {product.specifications}</p>}
        {product.coolingCapacity && <p><strong>Cooling Capacity:</strong> {product.coolingCapacity}</p>}
        {product.serialNo && <p><strong>Serial No:</strong> {product.serialNo}</p>}
        {product.partNo && <p><strong>Part No:</strong> {product.partNo}</p>}
      </div>
    );
  };

  const renderCombinedView = () => {
    const schedules = quotation?.schedules || [];
    const groups = buildCombinedMergedGroups();
    const hasAnyVat = groups.some((gr) => gr.items.some((it) => Object.values(it.bySchedule).some((d) => (d.vatAmount ?? 0) !== 0)));
    const showVat = schedules.some((s: any) => s.vatInclusion === false) && hasAnyVat;
    const usdToBdtEnabled = quotation?.usdToBdtEnabled && (quotation?.usdToBdtRate ?? 0) > 0;
    const usdToBdtRate = Number(quotation?.usdToBdtRate) || 0;
    const numDataCols = 2 + schedules.length * 2 + (showVat ? 2 : 0) + (usdToBdtEnabled ? 1 : 0);
    const itemsColSpan = 1 + numDataCols;

    return (
      <div className="w-full">
        {renderDocumentInfo()}
        <h2 className="text-sm font-bold mb-3 pb-1 border-b border-black">Combined Quotation</h2>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-black text-[10px]">
            <thead>
              <tr className="bg-gray-50 print:bg-white">
                <th className="border border-black p-1 w-10 text-center">Sl.</th>
                <th className="border border-black p-1 text-left min-w-[140px]">Items</th>
                <th className="border border-black p-1 w-12 text-center">Unit</th>
                <th className="border border-black p-1 w-10 text-center">Qty</th>
                {schedules.map((s: any, i: number) => {
                  const cur = s.currency === 'USD' ? 'USD' : 'BDT';
                  return (
                    <th colSpan={2} key={i} className="border border-black p-1 text-center">{s.scheduleName || `Schedule ${i + 1}`} ({cur})</th>
                  );
                })}
                {showVat && (
                  <>
                    <th className="border border-black p-1 w-12 text-right">VAT/unit</th>
                    <th className="border border-black p-1 w-12 text-right">VAT Amt</th>
                  </>
                )}
                {usdToBdtEnabled && <th className="border border-black p-1 w-14 text-right">USD to BDT</th>}
              </tr>
              <tr className="bg-gray-50 print:bg-white">
                <th className="border border-black p-1" colSpan={4} />
                {schedules.map((_: any, i: number) => (
                  <React.Fragment key={i}>
                    <th className="border border-black p-1 w-12 text-right">Rate</th>
                    <th className="border border-black p-1 w-14 text-right">Total</th>
                  </React.Fragment>
                ))}
                {showVat && <th className="border border-black p-1" colSpan={2} />}
                {usdToBdtEnabled && <th className="border border-black p-1" />}
              </tr>
            </thead>
            <tbody>
              {groups.map((group, sectionIdx) => (
                <React.Fragment key={`sec-${sectionIdx}`}>
                  <tr className="bg-gray-100 print:bg-gray-50 avoid-break">
                    <td className="border border-black p-1 text-center font-bold align-middle">{sectionIdx + 1}.0</td>
                    <td colSpan={itemsColSpan} className="border border-black p-1 font-bold break-words align-middle">
                      {group.sectionName}
                    </td>
                  </tr>
                  {group.sectionDescription ? (
                    <tr className="avoid-break">
                      <td className="border border-black p-1" />
                      <td colSpan={itemsColSpan} className="border border-black p-1 italic text-[9px] break-words pl-4">
                        {group.sectionDescription}
                      </td>
                    </tr>
                  ) : null}
                  {group.items.map((item, itemIdx) => {
                    const firstData = Object.values(item.bySchedule)[0];
                    const firstQty = firstData?.qty != null && firstData.qty !== 0 ? firstData.qty : '';
                    let totalVatAmount = 0;
                    let totalUsdForRow = 0;
                    let vatOnRate: number | string = '-';
                    Object.entries(item.bySchedule).forEach(([scheduleIdxStr, d]) => {
                      const scheduleIdx = Number(scheduleIdxStr);
                      totalVatAmount += d.vatAmount ?? 0;
                      if (quotation?.schedules?.[scheduleIdx]?.currency === 'USD') totalUsdForRow += d.total ?? 0;
                      if (d.vatOnRate != null) vatOnRate = d.vatOnRate;
                    });
                    const usdToBdtVal = usdToBdtEnabled && totalUsdForRow > 0 ? usdToBdtRate * totalUsdForRow : null;
                    const fmt = (v: number | null | undefined) => (v != null && v !== 0 ? v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');
                    return (
                      <tr key={`${sectionIdx}-${itemIdx}-${item.product?._id}`} className="avoid-break">
                        <td className="border border-black p-1 text-center align-top">{sectionIdx + 1}.{itemIdx + 1}</td>
                        <td className="border border-black p-1 align-top break-words">
                          <p className="font-medium">{item.product?.name}</p>
                          {renderProductDetails(item.product)}
                        </td>
                        <td className="border border-black p-1 text-center align-top">{item.unit}</td>
                        <td className="border border-black p-1 text-center align-top">{firstQty}</td>
                        {schedules.map((_: any, scheduleIdx: number) => {
                          const d = item.bySchedule[scheduleIdx];
                          return (
                            <React.Fragment key={scheduleIdx}>
                              <td className="border border-black p-1 text-right align-top">{d ? fmt(d.unitRate) : ''}</td>
                              <td className="border border-black p-1 text-right align-top">{d ? fmt(d.total) : ''}</td>
                            </React.Fragment>
                          );
                        })}
                        {showVat && (
                          <>
                            <td className="border border-black p-1 text-right align-top">{(typeof vatOnRate === 'number' ? vatOnRate !== 0 : vatOnRate !== '-' && vatOnRate !== '') ? String(vatOnRate) : ''}</td>
                            <td className="border border-black p-1 text-right align-top">{fmt(totalVatAmount)}</td>
                          </>
                        )}
                        {usdToBdtEnabled && (
                          <td className="border border-black p-1 text-right align-top">{fmt(usdToBdtVal ?? undefined)}</td>
                        )}
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 mb-6 avoid-break text-xs">
          {(() => {
            const schedules = quotation?.schedules || [];
            if (schedules.length === 0) return null;
            return (
              <div className="space-y-1">
                {schedules.map((schedule: any, idx: number) => {
                  const totals = calculateScheduleTotals(schedule);
                  const currency = schedule.currency || 'BDT';
                  const scheduleName = schedule.scheduleName || `Schedule ${idx + 1}`;
                  if (totals.grandTotal <= 0) return null;
                  return (
                    <div key={idx} className="mb-1">
                      <p><strong>{scheduleName} - Total ({currency}):</strong> {totals.grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      <p className="ml-4 text-gray-600"><strong>Amount In Word ({currency}):</strong> {numberToWords(Math.floor(totals.grandTotal))} Only.</p>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
        {renderClosingStatement()}
      </div>
    );
  };

  // --- Sub-Components ---
  const renderDocumentInfo = () => (
    <div className="mb-6 text-xs font-sans">
      <div className="flex justify-between mb-4">
        <div>
           <p className="mb-1"><strong>Ref:</strong> {quotation.quotationNumber}</p>
           <p><strong>Date:</strong> {new Date(quotation.quotationDate).toLocaleDateString('en-GB')}</p>
        </div>
      </div>

      <div className="mb-4">
        <p className="font-bold mb-1">To</p>
        <p className="font-bold">{quotation.customer?.name || 'N/A'}</p>
        {quotation.customer?.designation && <p>{quotation.customer.designation}</p>}
        {quotation.customer?.company && <p className="font-medium">{quotation.customer.company}</p>}
        {quotation.customer?.address && (
          <p className="text-gray-600">
             {typeof quotation.customer.address === 'string' ? quotation.customer.address : JSON.stringify(quotation.customer.address)}
          </p>
        )}
      </div>

      <div className="mb-4">
        <p><strong>Project Name:</strong> {quotation.projectName || 'N/A'}</p>
        <p><strong>Project Address:</strong> {quotation.projectAddress || 'N/A'}</p>
      </div>

      <div className="mb-4">
        <p><strong>Subject:</strong> Offer for supply, installation, testing and commissioning of Air Conditioning system for above mentioned project.</p>
      </div>
    </div>
  );

  const renderClosingStatement = () => {
    if (!quotation?.footnote?.trim()) return null;
    return (
      <div className="mt-8 pt-4 border-t border-gray-300 quotation-footnote">
        <div className="text-xs space-y-1">
          {renderRichContent(quotation.footnote, 'text-xs')}
        </div>
      </div>
    );
  };

  const renderSummary = () => {
    const hasUSDSchedules = quotation.schedules?.some((s: any) => s.currency === 'USD');
    const schedules = quotation.schedules || [];

    return (
      <div className="w-full">
        {renderDocumentInfo()}
        <h2 className="text-sm font-bold mb-3 pb-1 border-b border-black">Quotation Summary</h2>

        <table className="w-full table-fixed border-collapse border border-black mb-6 text-xs">
          <thead>
            <tr className="bg-gray-50 print:bg-white">
              <th className="border border-black p-2 text-left w-1/4">Item</th>
              <th className="border border-black p-2 text-left w-1/2">Description of Items</th>
              {hasUSDSchedules && <th className="border border-black p-2 text-right w-[12.5%]">USD</th>}
              <th className="border border-black p-2 text-right w-[12.5%]">BDT</th>
            </tr>
          </thead>
          <tbody>
            {schedules.map((schedule: any, index: number) => {
              const totals = calculateScheduleTotals(schedule);
              return (
                <tr key={index} className="avoid-break">
                  <td className="border border-black p-2 font-bold break-words">{schedule.scheduleName || `Schedule ${index + 1}`}</td>
                  <td className="border border-black p-2 break-words">{schedule.scheduleDescription || schedule.scheduleDetails?.description || ''}</td>
                  {hasUSDSchedules && (
                    <td className="border border-black p-2 text-right break-words">{schedule.currency === 'USD' ? totals.grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}</td>
                  )}
                  <td className="border border-black p-2 text-right break-words">{schedule.currency === 'BDT' ? totals.grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="mb-6 avoid-break text-xs">
          {(() => {
            const currencyTotals = calculateOverallTotals();
            const elements: React.ReactNode[] = [];
            if (currencyTotals['BDT']) elements.push(<p key="bdt" className="mb-1"><strong>Amount In Word (BDT):</strong> {numberToWords(Math.floor(currencyTotals['BDT'].grand))} Only.</p>);
            if (currencyTotals['USD']) elements.push(<p key="usd"><strong>Amount In Word (USD):</strong> {numberToWords(Math.floor(currencyTotals['USD'].grand))} Only.</p>);
            return elements;
          })()}
        </div>

        {quotation.termsAndConditions && (
          <div>
            <h3 className="font-bold mb-2 text-xs">Terms and Conditions:</h3>
            <div className="text-xs space-y-1">
              {renderRichContent(quotation.termsAndConditions)}
            </div>
          </div>
        )}

        {renderClosingStatement()}
      </div>
    );
  };

  const renderScheduleDetails = (currentSchedule: any) => {
    const hasVAT = currentSchedule.sections?.some((section: any) =>
      section.products?.some((product: any) => product.vatAmount != null && product.vatAmount !== 0)
    );
    const currencyText = currentSchedule.currency === 'USD' ? 'USD' : 'BDT';
    const spanColumns = (hasVAT ? 8 : 6) - 1;
    const totals = calculateScheduleTotals(currentSchedule);
    const baseTotal = currentSchedule.vatInclusion ? totals.totalAmount : (totals.totalAmount + totals.totalVatAmount);

    return (
      <div className="w-full">
        {renderDocumentInfo()}

        <h2 className="font-bold mb-3 text-sm">
          {currentSchedule.scheduleName || `Schedule ${(activeView as number) + 1}`}
          {currentSchedule.scheduleDescription && `: ${currentSchedule.scheduleDescription}`}
        </h2>

        <table className="w-full table-fixed border-collapse border border-black mb-4 text-[11px]">
          <thead>
            <tr className="bg-gray-50 print:bg-white text-center">
              <th className="border border-black p-1 w-10">Sl.</th>
              <th className="border border-black p-1 text-left">Items</th>
              <th className="border border-black p-1 w-12">Unit</th>
              <th className="border border-black p-1 w-12">Qty</th>
              <th className="border border-black p-1 w-20 text-right">Rate ({currencyText})</th>
              <th className="border border-black p-1 w-24 text-right">Amount ({currencyText})</th>
              {hasVAT && (
                <>
                  <th className="border border-black p-1 w-16 text-right">VAT</th>
                  <th className="border border-black p-1 w-16 text-right">VAT Amt</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {currentSchedule.sections?.map((section: any, sectionIndex: number) => (
              <React.Fragment key={sectionIndex}>
                <tr className="bg-gray-100 print:bg-gray-50 section-header-row avoid-break">
                  <td className="border border-black p-1 text-center font-bold">{sectionIndex + 1}.0</td>
                  <td colSpan={spanColumns} className="border border-black p-1 font-bold break-words">{section.sectionName}</td>
                </tr>
                {section.sectionDescription && (
                   <tr className="avoid-break">
                     <td className="border border-black"></td>
                     <td colSpan={spanColumns} className="border border-black p-1 italic text-[10px] break-words">{section.sectionDescription}</td>
                   </tr>
                )}
                {section.products?.map((product: any, productIndex: number) => (
                  <tr key={productIndex} className="avoid-break">
                    <td className="border border-black p-1 text-center align-top">{sectionIndex + 1}.{productIndex + 1}</td>
                    <td className="border border-black p-1 align-top break-words">
                      <p className="font-medium">{product.product?.name}</p>
                      <div className="text-[9px] mt-1 space-y-0.5">
                        {product.product?.brand && (
                          <p>
                            <strong>Brand:</strong> {typeof product.product.brand === 'object' && product.product.brand !== null
                              ? product.product.brand.name
                              : product.product.brand}
                          </p>
                        )}
                        {product.product?.model && <p><strong>Model:</strong> {product.product.model}</p>}
                        {product.product?.type && (
                          <p>
                            <strong>Type:</strong> {typeof product.product.type === 'object' && product.product.type !== null
                              ? product.product.type.name
                              : product.product.type}
                          </p>
                        )}
                        {product.product?.compressorType && (
                          <p>
                            <strong>Compressor Type:</strong> {typeof product.product.compressorType === 'object' && product.product.compressorType !== null
                              ? product.product.compressorType.name
                              : product.product.compressorType}
                          </p>
                        )}
                        {product.product?.countryOfOrigin && (
                          <p>
                            <strong>Country of Origin:</strong> {typeof product.product.countryOfOrigin === 'object' && product.product.countryOfOrigin !== null
                              ? product.product.countryOfOrigin.name
                              : product.product.countryOfOrigin}
                          </p>
                        )}
                        {product.product?.countryOfAssembly && (
                          <p>
                            <strong>Country of Assembly:</strong> {typeof product.product.countryOfAssembly === 'object' && product.product.countryOfAssembly !== null
                              ? product.product.countryOfAssembly.name
                              : product.product.countryOfAssembly}
                          </p>
                        )}
                        {product.product?.details && <p><strong>Details:</strong> {product.product.details}</p>}
                        {product.product?.description && <p><strong>Description:</strong> {product.product.description}</p>}
                        {product.product?.specifications && <p><strong>Specifications:</strong> {product.product.specifications}</p>}
                        {product.product?.coolingCapacity && <p><strong>Cooling Capacity:</strong> {product.product.coolingCapacity}</p>}
                        {product.product?.serialNo && <p><strong>Serial No:</strong> {product.product.serialNo}</p>}
                        {product.product?.partNo && <p><strong>Part No:</strong> {product.product.partNo}</p>}
                      </div>
                    </td>
                    <td className="border border-black p-1 text-center align-top break-words">{product.unit || '-'}</td>
                    <td className="border border-black p-1 text-center align-top">{product.qty}</td>
                    <td className="border border-black p-1 text-right align-top">{product.unitRate?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                    <td className="border border-black p-1 text-right align-top">{product.total?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                    {hasVAT && (
                      <>
                        <td className="border border-black p-1 text-right align-top">{product.vatRate || product.vat || '-'}</td>
                        <td className="border border-black p-1 text-right align-top">{product.vatAmount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                      </>
                    )}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        <div className="mt-4 avoid-break flex justify-end">
          <div className="w-1/2">
            <table className="w-full border-collapse text-xs">
              <tbody>
                <tr className="border-b border-black">
                  <td className="py-1 text-right font-bold pr-2">Total {currencyText}:</td>
                  <td className="py-1 text-right pr-1">{baseTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
                {totals.cfrCtgAmount > 0 && (
                  <tr className="border-b border-black">
                    <td className="py-1 text-right font-bold pr-2">CFR CTG:</td>
                    <td className="py-1 text-right pr-1">{totals.cfrCtgAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                )}
                <tr className="border-t-2 border-b-2 border-black">
                  <td className="py-1 text-right font-bold pr-2">Grand Total:</td>
                  <td className="py-1 text-right font-bold pr-1">{totals.grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
              </tbody>
            </table>
            <div className="mt-2 text-[10px]">
              <p><strong>Amount in word:</strong> {currencyText}. {numberToWords(Math.floor(totals.grandTotal))} Only.</p>
            </div>
          </div>
        </div>

        {/* Terms and Conditions and Notes when summary is disabled */}
        {quotation.enableSummary === false && (
          <div className="mt-3 space-y-4">
            {quotation.termsAndConditions && (
              <div>
                <h3 className="font-bold mb-2 text-xs">Terms and Conditions:</h3>
                <div className="text-xs space-y-1">
                  {renderRichContent(quotation.termsAndConditions)}
                </div>
              </div>
            )}
            {quotation.notes && (
              <div>
                <h3 className="font-bold mb-2 text-xs">Notes:</h3>
                <div className="text-xs space-y-1">
                  {renderRichContent(quotation.notes)}
                </div>
              </div>
            )}
          </div>
        )}

        {renderClosingStatement()}
      </div>
    );
  };

  if (loading) return <div className="flex h-screen items-center justify-center"><LoadingSpinner size="lg" /></div>;
  if (!quotation) return <div className="p-8 text-center">Quotation not found</div>;

  const currentSchedule = typeof activeView === 'number' && quotation.schedules?.[activeView] ? quotation.schedules[activeView] : null;

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      <style>{`
        /* Footnote: same font size as Terms/Notes (0.75rem = text-xs), override rich HTML */
        .quotation-footnote,
        .quotation-footnote * {
          font-size: 0.75rem !important;
        }
      `}</style>
      <style>{`
        @media print {
          /* 1. RESET */
          #root { display: none !important; }
          body { 
             background: white !important; 
             margin: 0 !important; 
             padding: 0 !important; 
             overflow: visible !important; 
             -webkit-print-color-adjust: exact; 
             print-color-adjust: exact;
          }
          
          /* 2. PORTAL VISIBILITY */
          #print-portal-root { 
             display: block !important; 
             position: absolute; 
             top: 0; 
             left: 0; 
             width: 100%; 
             height: auto; 
             z-index: 99999; 
          }
          
          /* 3. PAGE SETUP - A4 landscape when printing combined view */
          @page { size: ${activeView === 'combined' ? 'A4 landscape' : 'A4'}; margin: 0; }

          /* 4. RESET PARAGRAPH MARGINS */
          p, h1, h2, h3, h4, h5, h6 { margin-top: 0; }

          /* 5. HYBRID LAYOUT - THE MAGIC FIX */
          .print-layout-table { width: 100%; border-collapse: collapse; position: relative; z-index: 50; }
          
          /* HEADER (Repeats automatically via THEAD) */
          .print-header-cell {
            height: 1.5in; 
            vertical-align: top;
            padding: 0;
          }
          .print-header-cell img {
            width: 100%;
            height: 1.5in;
            object-fit: contain;
            object-position: top;
            display: block;
          }

          /* FOOTER SPACER (Reserves space for FIXED footer overlay) */
          /* Height increased to 0.9in to pull content up by ~2 lines from previous 0.6in state */
          .print-footer-cell { 
             height: 0.9in; 
             vertical-align: bottom; 
             padding: 0;
          }

          /* FIXED FOOTER (Stays at bottom on all browsers, sits BEHIND content layer) */
          .print-footer-fixed {
            position: fixed;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 1.8in;
            z-index: -10; /* Behind content */
            display: flex !important;
            align-items: flex-end;
            justify-content: center;
            /* No background - allows text to flow over it if needed */
          }
          .print-footer-fixed img {
            width: 100%;
            height: 1.8in;
            object-fit: contain;
            object-position: bottom;
            display: block;
          }

          /* CONTENT PADDING */
          .print-content-cell {
            padding: 0 0.5in; 
            vertical-align: top;
          }

          /* TABLE MECHANICS */
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          .avoid-break { page-break-inside: avoid; break-inside: avoid; }
          .quotation-footnote, .quotation-footnote * { font-size: 0.75rem !important; }
        }
      `}</style>

      {/* --- SCREEN VIEW --- */}
      <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="sticky top-0 z-50 bg-white border-b px-6 py-4 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" icon={<ArrowLeft className="w-4 h-4" />} onClick={() => navigate('/quotations')}>Back</Button>
            <div><h1 className="text-lg font-bold">Quotation {quotation.quotationNumber}</h1></div>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" icon={<Printer className="w-4 h-4" />} onClick={handlePrint}>Print</Button>
          </div>
        </div>

        <div className="flex space-x-4 text-sm mb-4 border-t pt-4">
          <div className="flex items-center space-x-2">
            <input ref={headerInputRef} type="file" accept="image/*" onChange={handleHeaderImageUpload} className="hidden" />
            <Button variant="ghost" size="sm" icon={<Upload className="w-3 h-3"/>} onClick={() => headerInputRef.current?.click()}>
              {headerImage ? 'Change Header' : 'Upload Header'}
            </Button>
            {headerImage && <Button variant="ghost" size="sm" icon={<X className="w-3 h-3"/>} onClick={removeHeaderImage}>Remove</Button>}
          </div>
          <div className="flex items-center space-x-2">
             <input ref={footerInputRef} type="file" accept="image/*" onChange={handleFooterImageUpload} className="hidden" />
            <Button variant="ghost" size="sm" icon={<Upload className="w-3 h-3"/>} onClick={() => footerInputRef.current?.click()}>
              {footerImage ? 'Change Footer' : 'Upload Footer'}
            </Button>
            {footerImage && <Button variant="ghost" size="sm" icon={<X className="w-3 h-3"/>} onClick={removeFooterImage}>Remove</Button>}
          </div>
        </div>

        <div className="flex space-x-2 overflow-x-auto pb-2">
          {quotation.enableSummary !== false && (
            <button onClick={() => setActiveView('summary')} className={`px-3 py-1 text-sm rounded-md ${activeView === 'summary' ? 'bg-blue-100 text-blue-700' : 'text-gray-600'}`}>Summary</button>
          )}
          {quotation.combined && (
            <button onClick={() => setActiveView('combined')} className={`px-3 py-1 text-sm rounded-md ${activeView === 'combined' ? 'bg-blue-100 text-blue-700' : 'text-gray-600'}`}>Combined</button>
          )}
          {quotation.schedules?.map((s: any, i: number) => (
            <button key={i} onClick={() => setActiveView(i)} className={`px-3 py-1 text-sm rounded-md whitespace-nowrap ${activeView === i ? 'bg-blue-100 text-blue-700' : 'text-gray-600'}`}>{s.scheduleName || `Schedule ${i+1}`}</button>
          ))}
        </div>
      </motion.div>

      {/* PREVIEW CONTAINER */}
      <div className={activeView === 'combined' ? 'w-[297mm] mx-auto bg-white p-[0.5in] min-h-screen shadow-lg my-8 overflow-hidden' : 'w-[210mm] mx-auto bg-white p-[0.5in] min-h-screen shadow-lg my-8 overflow-hidden'}>
         <div className="border-b-2 border-dashed border-gray-300 mb-4 pb-2 text-center text-gray-400 text-sm">(Preview Mode){activeView === 'combined' && ' — Combined (A4 Landscape)'}</div>
         {activeView === 'summary' && quotation.enableSummary !== false ? renderSummary() : activeView === 'combined' ? renderCombinedView() : (currentSchedule && renderScheduleDetails(currentSchedule))}
      </div>

      {/* --- PRINT PORTAL --- */}
      <PrintPortal>
        <div className="hidden print:block font-sans">
          
          <table className="print-layout-table">
            <thead>
              <tr>
                {/* Header Image directly in THEAD ensures repetition on all browsers */}
                <td className="print-header-cell">
                   {headerImage && <img src={headerImage} alt="Header" />}
                </td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="print-content-cell">
                   {activeView === 'summary' && quotation.enableSummary !== false ? renderSummary() : activeView === 'combined' ? renderCombinedView() : (currentSchedule && renderScheduleDetails(currentSchedule))}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                {/* Spacer allows text to flow without hiding behind the Fixed Footer */}
                <td className="print-footer-cell">
                   <div style={{ height: '0.9in' }}></div>
                </td>
              </tr>
            </tfoot>
          </table>

          {/* Fixed Footer overlay - Solves "bottom of page" requirement globally */}
          <div className="print-footer-fixed">
            {footerImage && <img src={footerImage} alt="Footer" />}
          </div>
        </div>
      </PrintPortal>

    </div>
  );
};

export default ViewQuotationPage;